import type {
  AppContext, Assignment, AssignmentFilters, AuthResponse, DashboardData, OrganizationSummary, Professional, RegistrationPayload, Review, SessionData
} from '../types';
import { apiRequest } from './client';

/** Meldet ein Konto an; das zurückgegebene JWT wird anschliessend vom AuthProvider gespeichert. */
export function login(email: string, password: string): Promise<AuthResponse> {
  return apiRequest<AuthResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
}

/** Registriert ein Konto als Profi oder Organisation; die Antwort entspricht einem Login. */
export function register(payload: RegistrationPayload): Promise<AuthResponse> {
  return apiRequest<AuthResponse>('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

/** Prüft eine bestehende Sitzung und lädt alle erlaubten Arbeitskontexte. */
export function fetchSession(): Promise<SessionData> {
  return apiRequest<SessionData>('/api/v1/auth/me');
}

/** Lädt Kennzahlen und Tagesplan für den aktiven Kontext. */
export function fetchDashboard(context: AppContext): Promise<DashboardData> {
  return apiRequest<DashboardData>('/api/v1/dashboard', {}, context);
}

/** Lädt nur Professional-Profile, die zum aktiven Kontext gehören. */
export function fetchProfessionals(context: AppContext): Promise<Professional[]> {
  return apiRequest<Professional[]>('/api/v1/professionals', {}, context);
}

/** Lädt Organisationen, mit denen der aktive Kontext verbunden ist. */
export function fetchOrganizations(context: AppContext): Promise<OrganizationSummary[]> {
  return apiRequest<OrganizationSummary[]>('/api/v1/organizations', {}, context);
}

/** Sucht als Organization nach Professionals, die externe Anfragen zulassen. */
export function searchProfessionals(context: AppContext, params: {
  service?: string;
  radiusKm?: number;
  externalMode?: string;
  lat?: number;
  lng?: number;
}): Promise<Professional[]> {
  const query = new URLSearchParams();
  if (params.service && params.service !== 'all') query.set('service', params.service);
  if (params.radiusKm) query.set('radiusKm', String(params.radiusKm));
  if (params.externalMode && params.externalMode !== 'all') query.set('externalMode', params.externalMode);
  if (params.lat !== undefined) query.set('lat', String(params.lat));
  if (params.lng !== undefined) query.set('lng', String(params.lng));
  return apiRequest<Professional[]>(`/api/v1/professionals/search?${query.toString()}`, {}, context);
}

/** Lädt Assignments mit dem standardmässigen Filter «offen/bestätigt und aktuell». */
export function fetchAssignments(context: AppContext, filters: AssignmentFilters = { status: 'active', timeScope: 'current' }): Promise<Assignment[]> {
  const query = new URLSearchParams({ status: filters.status, timeScope: filters.timeScope });
  return apiRequest<Assignment[]>(`/api/v1/assignments?${query.toString()}`, {}, context);
}

/** Lädt Einzelbewertungen ohne berechnete Gesamtbewertung. */
export function fetchReviews(context: AppContext): Promise<Review[]> {
  return apiRequest<Review[]>('/api/v1/reviews', {}, context);
}
